/**
 * Layer A: Precise Move Classification
 * Calibrated thresholds similar to Lichess/Chess.com.
 * All values (delta, evalBefore, evalAfter) are in pawn units from mover's perspective.
 * Positive delta = mover improved the position.
 */
export function classifyMove(evals) {
    const { delta, evalBefore, evalAfter, isMate } = evals;
    const cpDelta = Math.round((delta ?? 0) * 100);
    const cpBefore = Math.round((evalBefore ?? 0) * 100);
    const cpAfter = Math.round((evalAfter ?? 0) * 100);
    const mateBefore = evals.mateBefore;
    const mateAfter = evals.mateAfter;
    // ── 0. Mate logic ─────────────────────────────────────────────────────────
    if (mateAfter !== undefined && mateAfter !== null) {
        if (mateAfter < 0) {
            // We are being mated after this move
            if (mateBefore !== undefined && mateBefore !== null && mateBefore < 0) {
                // We were already being mated before this move
                return mateAfter > mateBefore ? 'mistake' : 'best'; // worse if faster
            }
            return 'blunder'; // Allowed mate where there was none
        }
        if (mateAfter === 0)
            return 'best'; // Delivered immediate checkmate
        if (mateAfter > 0) {
            // We are delivering mate
            if (mateBefore !== null && mateBefore !== undefined && mateBefore > 0) {
                return mateAfter <= mateBefore ? 'best' : 'good'; // faster or same
            }
            return 'great'; // Found a new mate sequence
        }
    }
    // Position was losing to mate before and still is → blunder unless it got better
    if (isMate && cpAfter <= -900)
        return 'blunder';
    // ── Guard: missing delta → use 'good' as safe default ────────────────────
    if (delta === null || delta === undefined)
        return 'good';
    // ── Guard: evalBefore null → first move / unanalyzed prev position ────────
    if (evalBefore === null || evalBefore === undefined) {
        if (cpDelta <= -150)
            return 'blunder';
        if (cpDelta <= -80)
            return 'mistake';
        if (cpDelta <= -25)
            return 'inaccuracy';
        return 'good';
    }
    // ── 1. Loss detection (negative delta = mover worsened their position) ────
    if (cpDelta < -35) { // Threshold increased to ignore minor engine noise
        const absLoss = Math.abs(cpDelta);
        if (isMate && cpDelta < -200)
            return 'blunder';
        const switchesToLosing = cpBefore > 60 && cpAfter < -120;
        const switchesToDrawing = cpBefore > 120 && cpAfter < 25;
        if (absLoss >= 180 || switchesToLosing)
            return 'blunder';
        if (absLoss >= 90 || switchesToDrawing)
            return 'mistake';
        return 'inaccuracy';
    }
    // ── 2. Neutral / Best: small swing (engine noise range) ──────────────────
    if (Math.abs(cpDelta) <= 10)
        return 'best';
    // ── 3. Good / positive moves ──────────────────────────────────────────────
    if (cpAfter < -200)
        return 'good'; // Still clearly losing despite improvement
    if (cpDelta > 250 && cpBefore < -200 && cpAfter > -50)
        return 'brilliant'; // Escape from losing
    if (cpDelta > 100 && cpAfter > -100)
        return 'great';
    if (cpDelta > 40)
        return 'excellent';
    return 'good';
}
